import { ExecutorGroup } from "../../common/model/executor-group";
import { WebUser } from "../../common/model/webUser";
import { LocalDate } from "../../core/model/local-date";
import { ModelBase } from "../../core/model/model-base";
import { FaultNotificationPriority } from "../../fault-notification/model/const/enum";
import { Resource } from "../../fault-notification/model/resource";
import { LeaseAgreementStatus, LeaseAgreementStatusToString } from "../../rent/model/const/enums";
import { ObjectComponent } from "../../rent/model/object-component";
import { Property } from "../../rent/model/property";
import { ErrandStatus, ErrandStatusToString, ErrandType } from "../../round-planning/model/const/enums";
import { ErrandAction } from "../../round-planning/model/errand-action";

export class DwellingInspectionDeviationErrand extends ModelBase {
  constructor(dataContext?: DwellingInspectionDeviationErrand) {
    super();
    if (dataContext) {
      this.id = dataContext.id;
      this.dwellingInspectionDeviationIds = dataContext.dwellingInspectionDeviationIds;
      this.dwellingInspectionDeviationSelections = dataContext.dwellingInspectionDeviationSelections;
      this.errand = new DwellingInspectionErrand(dataContext.errand);
      this.components = dataContext.components.map(x => new ComponentDataModel(x));
      this.location = new DwellingInspectionDeviationErrandLocation(dataContext.location);
      this.origin = new DwellingInspectionDeviationErrandOrigin(dataContext.origin);
      this.contacts = dataContext.contacts?.map(x => new DwellingInspectionDeviationErrandContact(x)) ?? [];
    }
  }

  errand: DwellingInspectionErrand;
  dwellingInspectionDeviationIds: string[];
  dwellingInspectionDeviationSelections: DwellingInspectionDeviationErrandSelection[];
  components: ComponentDataModel[];
  location: DwellingInspectionDeviationErrandLocation;
  origin: DwellingInspectionDeviationErrandOrigin;
  contacts: DwellingInspectionDeviationErrandContact[];

  get attachmentsCount(): number {
    return this.errand?.attachments.length;
  }

  get shortDescription(): string {
    return this.errand?.description.length > 25 ? this.errand.description.substring(0, 25) + '...' : this.errand.description;
  }

  get statusString(): string {
    return ErrandStatusToString(this.errand.status);
  }

  get unreadDialogMessagesCount(): number {
    return this.errand?.unreadDialogMessagesCount ?? 0;
  }
}

export class ComponentDataModel {
  constructor(dataContext?: ComponentDataModel) {
    if (dataContext) {
      this.deviationId = dataContext.deviationId;
      this.component = dataContext.component;
      this.deviationDescription = dataContext.deviationDescription;
      this.roomCaption = dataContext.roomCaption;
      this.followUp = dataContext.followUp;
    }
  }

  deviationId: string;
  component: ObjectComponent;
  deviationDescription: string;
  roomCaption: string;
  followUp: boolean;
}

export class DwellingInspectionDeviationErrandLocation {
  constructor(dataContext: DwellingInspectionDeviationErrandLocation) {
    if (dataContext) {
      this.dwelling = dataContext.dwelling;
      this.building = dataContext.building;
      this.property = dataContext.property;
      this.dwellingId = dataContext.dwellingId;
      this.buildingId = dataContext.buildingId;
      this.propertyId = dataContext.propertyId;
    }
  }

  dwelling: string;
  building: string;
  property: string;
  dwellingId: string;
  buildingId: string;
  propertyId: string;
}

export class DwellingInspectionDeviationErrandContact {
  constructor(dataContext: DwellingInspectionDeviationErrandContact) {
    if (dataContext) {
      this.name = dataContext.name;
      this.phone = dataContext.phone;
      this.email = dataContext.email;
      this.isRepresentative = dataContext.isRepresentative;
      this.leaseAgreementStatus = dataContext.leaseAgreementStatus;
      this.leaseAgreementDateFrom = new LocalDate(
        dataContext.leaseAgreementDateFrom.year,
        dataContext.leaseAgreementDateFrom.month,
        dataContext.leaseAgreementDateFrom.day);
      this.leaseAgreementDateTo = dataContext.leaseAgreementDateTo
        ? new LocalDate(
        dataContext.leaseAgreementDateTo.year,
        dataContext.leaseAgreementDateTo.month,
        dataContext.leaseAgreementDateTo.day)
        : null;
    }
  }

  name: string;
  phone: string;
  email: string;
  leaseAgreementStatus: LeaseAgreementStatus;
  isRepresentative: boolean;
  leaseAgreementDateFrom: LocalDate;
  leaseAgreementDateTo: LocalDate | null;

  get leaseAgreementStatusText(): string {
    return LeaseAgreementStatusToString(this.leaseAgreementStatus);
  }
}

export class DwellingInspectionDeviationErrandOrigin {
  constructor(dataContext: DwellingInspectionDeviationErrandOrigin) {
    if (dataContext) {
      this.dwellingInspectionNumber = dataContext.dwellingInspectionNumber;
    }
  }

  dwellingInspectionNumber: string;
}

export class DwellingInspectionErrand extends ModelBase {
  constructor(dataContext?: DwellingInspectionErrand) {
    super();
    if (dataContext) {
      this.id = dataContext.id;
      this.errandNumber = dataContext.errandNumber;
      this.name = dataContext.name;
      this.description = dataContext.description;
      this.status = dataContext.status;
      this.type = dataContext.type;
      this.property = dataContext.property;
      this.webUser = dataContext.webUser;
      this.executorGroup = dataContext.executorGroup;
      this.attachments = dataContext.attachments;
      this.priority = dataContext.priority;
      this.resource = dataContext.resource;
      this.reportingEnabled = dataContext.reportingEnabled;
      this.resourcesForReporting = dataContext.resourcesForReporting?.map(x => new Resource(x)) ?? [];
      this.dialogId = dataContext.dialogId;
      this.unreadDialogMessagesCount = dataContext.unreadDialogMessagesCount ?? 0;

      this.actions = dataContext.actions ?? [];
      Object.defineProperties(this, {
        _actions: { enumerable: false, writable: true },
      });
    }
  }

  private _actions: ErrandAction[] = [];
  get actions(): ErrandAction[] {
    return this._actions;
  }
  set actions(items: ErrandAction[]) {
    this._actions = [];
    (items ?? []).forEach(item => this.addAction(item));
  }

  errandNumber: string;
  name: string;
  description: string;
  status: ErrandStatus;
  type: ErrandType;
  property: Property;
  webUser: WebUser;
  executorGroup: ExecutorGroup;
  priority: FaultNotificationPriority;
  attachments: any[];
  resource: WebUser;
  dialogId: string;
  unreadDialogMessagesCount: number = 0;
  // Optional tenant reference consumed by app-errand-action-list (string). Not populated
  // by this errand type, so the binding resolves to undefined as it did pre-strict.
  tenant?: string;

  reportingEnabled: boolean;
  resourcesForReporting: Resource[];

  addAction(context: any) {
    let action: ErrandAction = null;
    if (context instanceof ErrandAction) {
      action = context;
    } else {
      action = Object.assign(new ErrandAction(), context);
    }
    this._actions.push(action);
  }
}

export class DwellingInspectionDeviationErrandForm extends ModelBase {
  constructor(dataContext?: DwellingInspectionDeviationErrand, deletedAttachmentIds?: string[], includeActions: boolean = true) {
    super();
    if (dataContext) {
      this.id = dataContext.id;
      this.dwellingInspectionDeviationIds = dataContext.dwellingInspectionDeviationIds;
      this.dwellingInspectionDeviationSelections =
        dataContext.dwellingInspectionDeviationSelections?.length > 0
          ? dataContext.dwellingInspectionDeviationSelections
          : dataContext.components?.map(x => ({
            deviationId: x.deviationId,
            followUp: x.followUp,
          })).filter(x => !!x.deviationId) ?? [];
      this.errand = new DwellingInspectionErrandForm(dataContext.errand, includeActions);
      this.addedAttachments = dataContext.errand.attachments.filter(x => !x.id);

      const existingAttachments = dataContext.errand.attachments.filter(x => x.id);
      if (!dataContext.id && existingAttachments.length > 0) {
        this.deviationAttachmentIds = existingAttachments.map(x => x.id);
      }
    }

    this.deletedAttachmentIds = deletedAttachmentIds ?? [];
  }

  dwellingInspectionDeviationIds: string[];
  dwellingInspectionDeviationSelections: DwellingInspectionDeviationErrandSelection[];
  errand: DwellingInspectionErrandForm;
  addedAttachments: any[];
  deletedAttachmentIds: string[];
  deviationAttachmentIds: string[];

  toJSON() {
    return {
      ...this,
      addedAttachments: this.addedAttachments.map(() => ({})),
    };
  }
}

export type DwellingInspectionDeviationErrandSelection = {
  deviationId: string;
  followUp: boolean;
};

export class DwellingInspectionErrandForm extends ModelBase {
  constructor(dataContext?: DwellingInspectionErrand, includeActions: boolean = true) {
    super();
    if (dataContext) {
      this.id = dataContext.id;
      this.status = dataContext.status;
      this.description = dataContext.description;
      this.propertyId = dataContext.property?.id;
      this.priority = dataContext.priority;
      this.webUserId = dataContext.webUser?.id;
      this.executorGroupId = dataContext.executorGroup?.id;
      this.resourceId = dataContext.resource?.id;

      if (includeActions) {
        this.actions = dataContext.actions ?? [];
      }
    }
  }

  status: ErrandStatus;
  description: string;
  propertyId: string;
  priority: FaultNotificationPriority;
  webUserId: string;
  executorGroupId: string;
  resourceId: string;
  actions?: ErrandAction[];
}
